/**
 * Static marketing copy and journey data for the Kaizen Night landing page.
 * Single source of truth — hero chips, path card, and persona arcs stay aligned.
 */

export interface SuggestedGoal {
  label: string;
  prompt: string;
  pathIndex: number;
}

export interface JourneyPath {
  from: string;
  to: string;
  steps: readonly string[];
  nudges: readonly [string, string, string];
}

export interface PersonaStep {
  title: string;
  description: string;
}

export interface Persona {
  kanji: string;
  from: string;
  to: string;
  steps: readonly PersonaStep[];
  nudge: string;
  summary: readonly [string, string, string];
}

export const TYPEWRITER_TAGLINES: readonly string[] = [
  'The path clears as you move.',
  'Every step reveals the next one.',
  'Progress lives in motion.',
  'Clarity comes from moving forward.',
];

export const PROMPT_TAGLINE =
  'KΛI shapes the path around your pace, patterns, and goals.';

export const SUGGESTED_GOALS: readonly SuggestedGoal[] = [
  {
    label: 'Forward Deployed Engineer',
    prompt:
      "I'm a software developer and want to become a forward deployed engineer — embedded with customers, shipping solutions in the field. Build me a path.",
    pathIndex: 0,
  },
  {
    label: 'Security Engineering',
    prompt:
      "I'm a developer who wants to move into security engineering — finding vulnerabilities, threat modeling, and building secure systems. Where do I start?",
    pathIndex: 1,
  },
  {
    label: 'Machine Learning Engineering',
    prompt:
      "I'm a software developer who wants to become an ML engineer — training models, building pipelines, and shipping ML to production. Build me a path.",
    pathIndex: 2,
  },
];

export const JOURNEY_PATHS: readonly JourneyPath[] = [
  {
    from: 'Software Developer',
    to: 'Forward Deployed Engineer',
    steps: [
      'Technical discovery — understanding customer problems',
      'Solution design & rapid prototyping',
      'Deployment, integration & on-site delivery',
      'Stakeholder communication & building trust',
      'Own an outcome in production, end to end',
    ],
    nudges: [
      'FDE is code plus context. We start with listening before building.',
      "You're shipping in their environment now, not yours. That changes everything.",
      'Last step: one outcome you owned from first call to production.',
    ],
  },
  {
    from: 'Software Developer',
    to: 'Security Engineer',
    steps: [
      'Security mindset — threats, boundaries & failure modes',
      'OWASP Top 10 & vulnerabilities in real codebases',
      'Auth, secrets & supply chain hygiene',
      'Threat modeling & secure design reviews',
      'Ship code you can defend under scrutiny',
    ],
    nudges: [
      "Security isn't a checklist. It's thinking like an attacker first.",
      'You just threat-modeled a real system. Most devs never do.',
      "Final step: code you'd stand behind in a security review.",
    ],
  },
  {
    from: 'Software Developer',
    to: 'ML Engineer',
    steps: [
      'ML foundations — data, loss functions & evaluation',
      'Training models — from notebook to pipeline',
      'Feature engineering & data quality',
      'Model deployment & monitoring in production',
      'Ship a model that survives the real world',
    ],
    nudges: [
      'You already ship software. ML adds a loop: data in, model out, measure, repeat.',
      "Notebooks don't scale. You're building the pipeline now.",
      'Last step: a model in production with monitoring you trust.',
    ],
  },
];

export const PERSONAS: readonly Persona[] = [
  {
    kanji: '道',
    from: 'Software Developer',
    to: 'Forward Deployed Engineer',
    steps: [
      {
        title: 'Technical discovery',
        description: 'FDE starts with listening — you already ship code fast.',
      },
      {
        title: 'Solution design & rapid prototyping',
        description: 'Short cycles, real constraints, no slide decks.',
      },
      {
        title: 'On-site delivery & integration',
        description: 'Their environment, not your laptop.',
      },
      {
        title: 'Own the outcome end to end',
        description: 'From first call to production.',
      },
    ],
    nudge:
      'FDE is code plus context. Your shipping instincts are the foundation — we build the field skills on top.',
    summary: [
      'From shipping features to ',
      'owning outcomes in the field',
      ' — every step shaped around how you already work.',
    ],
  },
  {
    kanji: '盾',
    from: 'Software Developer',
    to: 'Security Engineer',
    steps: [
      {
        title: 'Security mindset',
        description:
          'Think like an attacker before you write the next feature.',
      },
      {
        title: 'OWASP & real vulnerabilities',
        description: 'Not theory — bugs in codebases like yours.',
      },
      {
        title: 'Auth, secrets & supply chain',
        description: 'The hygiene that stops breaches early.',
      },
      {
        title: 'Threat modeling & secure design',
        description: 'Ship code you can defend under scrutiny.',
      },
    ],
    nudge:
      'You already know how systems are built. Security is learning how they break — then preventing it.',
    summary: [
      'From writing features to ',
      'defending systems',
      ' — built on the code instincts you already have.',
    ],
  },
  {
    kanji: '学',
    from: 'Software Developer',
    to: 'ML Engineer',
    steps: [
      {
        title: 'ML foundations',
        description:
          'Data, loss functions, evaluation — the vocabulary of models.',
      },
      {
        title: 'Training pipelines',
        description: 'From notebook experiments to repeatable code.',
      },
      {
        title: 'Feature engineering & data quality',
        description: "Garbage in was never the model's fault.",
      },
      {
        title: 'Deploy & monitor in production',
        description: 'A model that survives the real world.',
      },
    ],
    nudge:
      'You ship software. ML adds a loop: data in, model out, measure, repeat. Your code skills carry over.',
    summary: [
      'From writing code to ',
      'shipping models',
      " — every step paced for a developer's instincts.",
    ],
  },
];

export const SIGNAL_PILLS: readonly { before: string; emphasis: string }[] = [
  { before: 'Reread a section? — ', emphasis: 'a deeper analogy appears' },
  {
    before: 'Learn best late at night? — ',
    emphasis: 'steps adapt to your hours',
  },
  { before: 'Learn by building? — ', emphasis: 'more exercises, less theory' },
  { before: 'Show up day after day — ', emphasis: 'momentum compounds' },
  {
    before: 'Skip what you know — ',
    emphasis: 'the path trusts you and moves on',
  },
];
